<template>
  <div data-testid="rx-step-lensquality">
    <div class="mx-auto flex h-full flex-col space-y-6 pt-6">
      <div class="px-6 text-center">
        <SupportHeadline
          v-if="isOverlayAvailable"
          :text="linkText"
          data-testid="rx-lensquality-overlay"
          @click="showOverlay"
        />
      </div>

      <div class="mx-7 flex justify-center gap-1 md:hidden">
        <button
          v-for="(lens, lIndex) in lensQualities"
          :key="lIndex"
          class="rounded-xl bg-shade-200 px-4 py-2 text-sm font-medium transition hover:bg-shade-300"
          @click="scrollItemIntoView(lens.referenceKey)"
        >
          {{ lens.title }}
        </button>
      </div>

      <div class="flex-1 overflow-x-hidden pb-2">
        <div
          ref="sliderContainer"
          class="snap-x snap-mandatory overflow-x-auto pb-8"
        >
          <!-- grid -->
          <div
            class="mx-auto grid gap-x-2 px-4"
            :style="{
              gridTemplateColumns: `repeat(${lensQualities.length}, minmax(0, 1fr))`,
              gridTemplateRows: `repeat(${maxColumns}, auto)`,
              width: `${lensQualities.length * 360}px`,
            }"
          >
            <!-- subgrid for highlighting recommendations -->
            <template
              v-for="(lenses, group, gIndex) in groupedLensQualities"
              :key="group"
            >
              <div
                v-if="lenses.length"
                class="grid grid-cols-subgrid grid-rows-subgrid rounded-xl"
                :class="{
                  'bg-orange-100 p-2': group === 'recommended',
                }"
                :style="{
                  gridColumn: `span ${lenses.length}`,
                  gridRow: `span ${maxColumns}`,
                }"
              >
                <div
                  v-if="group === 'recommended'"
                  class="row-start-1 pb-2 text-center font-medium"
                  :style="{
                    gridColumn: `1 / span ${lenses.length}`,
                  }"
                >
                  <!-- recommendation headline -->
                  <div
                    data-testid="rx-lens-quality-recommendation"
                    class="sticky left-[10vw] flex max-w-[80vw] justify-center text-balance rounded-xl p-2 leading-snug"
                  >
                    <p>
                      {{ $t('rxConfigurator.lensQuality.recommendation') }}
                    </p>
                  </div>
                </div>
                <RxSelectOption
                  v-for="(lens, lIndex) in lenses"
                  :key="lIndex"
                  class="row-start-2 grid snap-center grid-rows-subgrid"
                  :class="{
                    'bg-blue-100': lens.isRecommendation,
                    'snap-start': gIndex + lIndex === 0,
                    'snap-end': gIndex + lIndex === lensQualities.length - 1,
                  }"
                  :style="{
                    gridRowEnd: `span ${lens.baseUsp.length + 3}`,
                  }"
                  :title="lens.title"
                  :selected="selectedOption === lens.referenceKey"
                  :data-testid="`rx-lensquality-${lens.referenceKey}`"
                  show-price
                  :price="lens.isInclusive ? 0 : lens.price"
                  @click:select="selectOption(lens)"
                >
                  <template #direct>
                    <div
                      ref="sliderItems"
                      class="grid grid-rows-subgrid"
                      :style="{
                        gridRow: `span ${lens.baseUsp.length + 2}`,
                      }"
                    >
                      <div
                        v-if="lens.infoText"
                        v-dompurify-html="removeBreaks(lens.infoText)"
                        class="px-6 pb-4 text-sm font-normal text-shade-600"
                      />
                      <div
                        class="relative flex flex-col items-center justify-center border-t border-shade-300 p-1"
                      >
                        <!-- on top usp -->
                        <div
                          v-if="lens.onTopUsp.length >= 1"
                          class="absolute left-6 top-2"
                        >
                          <span
                            v-dompurify-html="lens.onTopUsp[0].label"
                            class="rounded bg-orange-400 px-3 py-1 text-sm font-medium"
                          ></span>
                        </div>
                        <ProductImage
                          v-if="lens.image"
                          :image="lens.image"
                          class="aspect-2/3 mb-6 mt-8 w-28"
                        />
                        <span
                          class="mb-1 mt-2 inline-block text-center text-xs text-shade-400"
                        >
                          {{ $t('pdp.lens_option.example') }}
                        </span>
                      </div>
                      <!-- usp -->
                      <ul
                        class="grid grid-rows-subgrid px-1 pb-1"
                        :style="{
                          gridRow: `span ${lens.baseUsp.length}`,
                        }"
                      >
                        <li
                          v-for="(feature, fIndex) in lens.baseUsp"
                          :key="fIndex"
                          class="flex gap-2 border-t border-shade-300 p-3 last:rounded-b"
                          :class="{
                            'border-white bg-red-100': !isPositiveUsp(feature),
                          }"
                        >
                          <IconFielmannCheck
                            v-if="isPositiveUsp(feature)"
                            class="size-5 shrink-0 text-green-700"
                          />
                          <IconFielmannCancel
                            v-else
                            class="size-5 shrink-0 text-red-600"
                          />
                          <span
                            v-dompurify-html="feature.label"
                            class="text-base"
                          ></span>
                        </li>
                      </ul>
                    </div>
                  </template>
                </RxSelectOption>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>

  <Overlay
    v-if="isOverlayAvailable"
    v-model:open="isOverlayOpen"
    :title="overlayTitle"
    portal-target="#rx-lens-quality-portal"
    layout="right"
    enter-from="right"
  >
    <div v-for="content in overlayContent" :key="content._uid">
      <component :is="content.component" :blok="content" />
    </div>
  </Overlay>
</template>

<script setup lang="ts">
const emit = defineEmits(['ui:update'])

const { lensQualities, setLensQuality, resetTintingType, state } =
  await useRxConfiguratorState()

const { hasValidPrescriptionValues } = useRxPrescription()

/** lens options */
const selectedOption = ref<SingleOption['referenceKey'] | null>(
  state.value?.lensQuality?.variant?.referenceKey || null,
)

const selectOption = (lens: LensOption) => {
  selectedOption.value = lens.referenceKey
  setLensQuality(lens.variantId)

  // reset tinting in case the customer changes the lens quality afterwards
  resetTintingType()
}

const firstRecommendedOption = computed(
  () =>
    lensQualities.value.find((lens) => lens.isRecommendation) ||
    lensQualities.value.find((lens) => !lens.isInclusive) ||
    lensQualities.value[1],
)

const groupedLensQualities = computed(() => {
  // group lenses in basic and recommended
  const firstRecommendedOptionIndex = hasValidPrescriptionValues.value
    ? lensQualities.value.findIndex(
        (lens) =>
          lens.referenceKey === firstRecommendedOption.value.referenceKey,
      )
    : Infinity
  return {
    basic: lensQualities.value.slice(0, firstRecommendedOptionIndex),
    recommended: lensQualities.value.slice(firstRecommendedOptionIndex),
  }
})

const selectDefaultOption = () => {
  if (selectedOption.value) return

  selectOption(firstRecommendedOption.value)
}

onMounted(() => {
  selectDefaultOption()
  emit('ui:update', {
    mainAction: 'continue' as RxMainAction,
  })
  scrollItemIntoView(selectedOption.value)
})

/** grid */

const maxColumns = computed(() => {
  const maxUspLength = Math.max(
    ...lensQualities.value.map((lens) => lens.baseUsp.length),
  )
  return 4 + maxUspLength
})

/** helpers */

const isPositiveUsp = (feature: LensOption['baseUsp'][0]) => {
  return !feature?.icon?.includes('red')
}

const removeBreaks = (str: string) => str.replace(/<br\s*\/?>/gi, '')

/** slider */

const sliderContainer = useTemplateRef('sliderContainer')
const sliderItems = useTemplateRef<HTMLDivElement[]>('sliderItems')

const scrollItemIntoView = (
  referenceKey: SingleOption['referenceKey'] | null,
) => {
  if (!referenceKey || !sliderItems?.value || !sliderContainer?.value) return

  const scrollToIndex = lensQualities.value.findIndex(
    (lens) => lens.referenceKey === referenceKey,
  )
  const scrollTo = sliderItems.value[scrollToIndex]
  if (!scrollTo) return

  sliderContainer.value?.scrollTo({
    left: scrollTo.offsetLeft - sliderContainer.value.offsetLeft,
    behavior: 'smooth',
  })
}

/** overlays */

const {
  overlayTitle,
  overlayContent,
  linkText,
  open: isOverlayOpen,
  show: showOverlay,
  available: isOverlayAvailable,
} = await useOverlay(RxOverlays.LENS_QUALITY)
</script>
